import { useEffect, useState } from 'react';
import { useUnit } from 'effector-react';
import { Controller, useForm } from 'react-hook-form';
import { $initialAccountsStore, $profileFeatureStore } from '@lib/store';
import { AccountUtils, FormatUtils } from '@shared/utils';
import type { UserProfileResponse, UserResponse, UserWithPaymentInfo } from '@shared/api/rest/users';
import { AccountType, type AccountResponse } from '@shared/api/rest/accounts';
import { Button, ErrorMessage, Modal, Multiselect } from '@shared/widgets';
import { useTranslation } from 'react-i18next';

interface IForm {
  accounts: number[];
}
const DEFAULT_VALUES: IForm = { accounts: [] };

interface IAddAccountsProps {
  isOpen: boolean;
  user: UserWithPaymentInfo | UserResponse | UserProfileResponse;
  accountsItems: AccountResponse[];
  onClose: () => void;
  onSubmit: (data: { userIds: string; accountIds: number[] }) => void;
}

export const AddAccountsModal = (props: IAddAccountsProps) => {
  const profileFeatureStore = useUnit($profileFeatureStore.store);
  const userAccountsStore = useUnit($initialAccountsStore.store);

  const { control, formState, handleSubmit, reset } = useForm<IForm>({ defaultValues: DEFAULT_VALUES });
  const { t } = useTranslation();

  const [accountList, setAccountList] = useState<AccountResponse[]>([]);

  useEffect(() => {
    if (props.isOpen && profileFeatureStore.data?.accountFlows) {
      const result = AccountUtils.filterAccounts(profileFeatureStore.data.accountFlows, userAccountsStore.data);
      result && setAccountList(result);
    }
  }, [props.isOpen, userAccountsStore.data]);

  const closeHandler = () => {
    props.onClose();
    reset(DEFAULT_VALUES);
  };
  const submitHandler = (data: IForm) => {
    props.onSubmit({ userIds: props.user.id, accountIds: data.accounts });
    closeHandler();
  };

  return (
    <Modal isOpen={props.isOpen} onClose={closeHandler} title={t('modals.addAccounts.title')}>
      <form className='flex flex-col gap-6' noValidate onSubmit={handleSubmit(submitHandler)}>
        <Controller
          name='accounts'
          control={control}
          rules={{ required: t('errors.form.required', { ns: 'common' }) }}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <div>
              <Multiselect
                isLoading={userAccountsStore.loading}
                disabled={userAccountsStore.loading}
                items={accountList
                  .filter((acc) => !props.accountsItems.map((a) => a.id).includes(acc.id))
                  .map((acc) => ({
                    value: acc.id,
                    label: acc.type === AccountType.Payment ? acc.accountNumber : FormatUtils.toSliceNumber(acc.accountNumber),
                  }))}
                required
                nullValue={{ isEnable: false, text: t('placeholders.chooseAccounts') }}
                value={value}
                onChange={onChange}
              />
              <ErrorMessage text={error?.message} />
            </div>
          )}
        />
        <Button fullWidth color='primary' type='submit' disabled={formState.isSubmitting}>
          {t('buttons.add')}
        </Button>
      </form>
    </Modal>
  );
};
